import React, { useContext } from 'react';

import { CurrencyCode } from '@watershed/constants/currency';
import { OnboardingKind } from '@watershed/constants/onboarding';
import {
  Region,
  WatershedPlan,
  WatershedPlanLegacy,
} from '@watershed/constants/organization';
import {
  PermissionObjectType,
  PermissionType,
} from '@watershed/constants/permissions';

interface UserContextData {
  orgId: string;
  orgName: string;
  orgHelpChannel: string | null;
  orgCountry: string | null;
  demoOrg: boolean;
  testOrg: boolean;
  stagingOrg: boolean | null;
  region: Region;
  watershedPlanLegacy: WatershedPlanLegacy;
  watershedPlan: WatershedPlan;
  watershedPlanIncludesMeasurement: boolean;
  watershedPlanIncludesReductions: boolean;
  watershedPlanIncludesDatasetCadences: boolean;
  watershedPlanIncludesReporting: boolean;
  userId: string;
  createdAt: Date;
  accessibleOrgs: Array<{
    companyId: string;
    id: string;
    name: string;
    demoOrg: boolean;
    testOrg: boolean;
    stagingOrg: boolean;
    canAccessInCurrentSession: boolean;
  }>;
  userName: string;
  userEmail: string;
  userIsWatershedEmployee: boolean;
  userIsWatershedContractor: boolean;
  userIsE2ETester: boolean;
  permissions: Array<{
    permission: PermissionType;
    objectId?: string | null;
    objectType?: PermissionObjectType | null;
  }>;
  userOnboardingsCompleted: Array<OnboardingKind>;
  logoUrl: string | null;
  iconUrl: string | null;
  loginAsUser: { id: string; name: string } | null;
  isCustomer: boolean;
  hasInitialized: boolean;

  orgFiscalYearStartMonth: number | null;
  canAccessCorporate: boolean;
  canAccessFinance: boolean;
  currency: CurrencyCode | null;
  companyId: string;
  domains: Array<string>;
  sessionId: string;
  preferredLocale: string | null;
  sessionTimeoutMinutes: number | null;
}

export interface UserContextProps extends UserContextData {}

const UserContextDataDefaults: UserContextData = {
  orgId: 'string',
  orgName: 'string',
  orgHelpChannel: null,
  orgCountry: null,
  demoOrg: false,
  testOrg: false,
  stagingOrg: false,
  region: Region.Us,
  watershedPlan: WatershedPlan.Standard,
  watershedPlanLegacy: WatershedPlanLegacy.Standard,
  watershedPlanIncludesMeasurement: false,
  watershedPlanIncludesReductions: false,
  watershedPlanIncludesDatasetCadences: false,
  watershedPlanIncludesReporting: false,
  userId: 'string',
  createdAt: new Date(),
  accessibleOrgs: [],
  userName: 'string',
  userEmail: 'string',
  userIsWatershedEmployee: false,
  userIsWatershedContractor: false,
  userIsE2ETester: false,
  permissions: [],
  userOnboardingsCompleted: [],
  logoUrl: null,
  iconUrl: null,
  loginAsUser: null,
  isCustomer: true,
  hasInitialized: false,
  orgFiscalYearStartMonth: null,
  canAccessCorporate: true,
  canAccessFinance: false,
  currency: null,
  companyId: '',
  domains: [],
  sessionId: '',
  preferredLocale: null,
  sessionTimeoutMinutes: null,
};

const UserContextDefaults = UserContextDataDefaults;

const UserContext = React.createContext<UserContextProps>(UserContextDefaults);

/**
 * If you're using this from a test, instead use `MockUserContextProvider` to
 * better provide defaults and overrides.
 */
export function UserContextProvider({
  parsedUserContext,
  children,
}: React.PropsWithChildren<{ parsedUserContext: UserContextProps }>) {
  return (
    <UserContext.Provider value={parsedUserContext}>
      {children}
    </UserContext.Provider>
  );
}

/**
 * Used from Storybook + unit tests to mock out user context provider with the
 * defaults, but allows override values. DO NOT use from production code.
 */
function MockUserContextProvider({
  overrideUserContext,
  children,
}: {
  overrideUserContext?: Partial<UserContextProps>;
  children: React.ReactNode;
}) {
  return (
    <UserContext.Provider
      value={{
        ...UserContextDataDefaults,
        ...overrideUserContext,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  return useContext(UserContext);
}

export function useUserIsWatershedEmployeeOrContractor() {
  const { userIsWatershedContractor, userIsWatershedEmployee } =
    useUserContext();
  return userIsWatershedContractor || userIsWatershedEmployee;
}

export function useIsSupplierOrg() {
  const userContext = useUserContext();
  return userContext.watershedPlanLegacy === WatershedPlanLegacy.NoPlan;
}

export const testingExports = {
  MockUserContextProvider,
};
